// ABOUTME: Where realtime's discussion events go: an open feed takes its share of a batch, the badges of the items in
// ABOUTME: the loaded window and of the athletes on screen are read again, and a grown unread count is told.
import type { ApolloClient } from "@apollo/client"
import { isOwned, type CalendarScope } from "@/model/calendar/cache/recordOwners"
import { runSerial } from "@/model/calendar/cache/runSerial"
import { createLogger } from "@/model/logger"
import type { DiscussionChangedEvent, RealtimeEvent } from "@/model/realtime/events"
import { cachedSummary, writeNewerSummary } from "./discussionCache"
import { forgetHiddenDraft } from "./messageDrafts"
import { fetchSummary } from "./summary"

const logger = createLogger("Discussion")

/**
 * A burst of messages costs one read of the athletes' list, as a burst of calendar events costs one
 * refresh of the window (REFRESH_MIN_INTERVAL_MS).
 */
export const ATHLETE_BADGES_MIN_INTERVAL_MS = 5000

/** An open feed, as the events of its item reach it. */
export interface OpenFeed {
  /** A message was written. Answers whether the feed reads — the answer brings the counters too. */
  posted: (seq: number) => boolean
  /** A message was edited: the loaded range is read again. */
  edited: () => void
  /** A message was deleted: it leaves the list, nothing is read. */
  deleted: (seq: number) => void
  /** The item went, and its discussion with it. */
  removed: () => void
  /** Events were missed: the loaded range is read again. */
  reload: () => void
}

const feeds = new Map<string, Set<OpenFeed>>()
const athleteBadges = new Set<() => Promise<unknown>>()
const newMessageListeners = new Set<(itemId: string) => void>()

/** An open feed takes the events of its item while it is mounted. */
export function registerOpenFeed(itemId: string, feed: OpenFeed): () => void {
  const set = feeds.get(itemId) ?? new Set()
  set.add(feed)
  feeds.set(itemId, set)
  return () => {
    set.delete(feed)
    if (set.size === 0) feeds.delete(itemId)
  }
}

/** The athletes' list reads itself again — the badges with it — while it is on screen. */
export function registerAthleteBadges(refresh: () => Promise<unknown>): () => void {
  athleteBadges.add(refresh)
  return () => {
    athleteBadges.delete(refresh)
  }
}

/**
 * Told when the unread count of an item in the loaded window grew — somebody else wrote: one's own
 * messages from another device leave it as it was. Not for an item whose feed is open here: the
 * message shows there.
 */
export function subscribeNewMessages(listener: (itemId: string) => void): () => void {
  newMessageListeners.add(listener)
  return () => {
    newMessageListeners.delete(listener)
  }
}

function refreshAthleteBadges(): void {
  if (athleteBadges.size === 0) return
  // runSerial never rejects: a failed read is logged there (runSerial.ts:116-117, :148).
  void runSerial(
    "discussion-athlete-badges",
    undefined,
    () => Promise.all([...athleteBadges].map((refresh) => refresh())),
    { minIntervalMs: ATHLETE_BADGES_MIN_INTERVAL_MS },
  )
}

/**
 * On screen now: its day in a mounted calendar — the window or the coach's batch (recordOwners) — or its
 * feed open. Counters a week visited earlier left in the cache are nobody's business here. Asked when an
 * event comes, again before the read it asks for — which may wait its turn — and again before telling.
 */
const isOnScreen = (itemId: string, scopes: readonly CalendarScope[]): boolean =>
  feeds.has(itemId) || scopes.some((scope) => isOwned(scope))

/**
 * Reads the item's counters again, one read at a time per item: a deletion moves neither readSeq nor
 * lastSeq, so an earlier answer landing last could only be told apart by its count. Reads asked for
 * meanwhile go as one, after it, for every day the item was seen on.
 */
function refreshBadge(client: ApolloClient, itemId: string, scopes: readonly CalendarScope[]): void {
  void runSerial(
    `discussion-badge:${itemId}`,
    scopes,
    (queued, isStale) => readBadge(client, itemId, queued, isStale),
    { merge: (queued, incoming) => [...queued, ...incoming] },
  )
}

/**
 * Reads the item's counters (`limit: 0`), and tells when the unread count grew. A read that outlived
 * runSerial's stall timeout has lost its turn to the one after it (runSerial.ts:107-111): it writes nothing.
 */
async function readBadge(
  client: ApolloClient,
  itemId: string,
  scopes: readonly CalendarScope[],
  isStale: () => boolean,
): Promise<void> {
  // Left the screen while the read waited its turn — the coach went to another athlete: nothing shows it.
  if (!isOnScreen(itemId, scopes)) return
  const before = cachedSummary(client.cache, itemId)?.unreadCount ?? 0
  try {
    const summary = await fetchSummary(client, itemId)
    if (isStale()) return
    writeNewerSummary(client.cache, summary)
  } catch (error: unknown) {
    // Gone or out of reach meanwhile: the calendar's own event takes the card away.
    logger.warn("A discussion badge was not read again; the next event or the calendar's poll will", { itemId, error })
    return
  }
  const after = cachedSummary(client.cache, itemId)?.unreadCount ?? 0
  // Told only of an item still on screen, and not of one whose feed is open by now: the message shows there.
  const isTold = after > before && !feeds.has(itemId) && scopes.some((scope) => isOwned(scope))
  if (isTold) newMessageListeners.forEach((listener) => listener(itemId))
}

/**
 * One item's share of a batch, by the rules of `frontend-discussion.md` §7: an edit is read by
 * replacement, a new message by reading on, a deletion needs no read; a feed not at its end only
 * gets its counters. An item gone with its discussion closes its feed and has no badge left.
 */
function forItem(client: ApolloClient, itemId: string, events: readonly DiscussionChangedEvent[]): void {
  const open = feeds.get(itemId)
  const reasons = new Set(events.map((event) => event.reason))
  if (reasons.has("removed")) {
    closeFeedsOf(itemId)
    return
  }
  let feedReads = false
  if (open) {
    for (const event of events) {
      const { seq } = event
      if (event.reason === "deleted" && seq !== null) open.forEach((feed) => feed.deleted(seq))
    }
    const newest = Math.max(...events.filter((event) => event.reason === "posted").map((event) => event.seq ?? 0))
    if (reasons.has("edited")) {
      open.forEach((feed) => feed.edited())
      feedReads = true
    } else if (newest > 0) {
      open.forEach((feed) => {
        if (feed.posted(newest)) feedReads = true
      })
    }
  }
  // A read is read again too, the feed open or not: it reaches every device of the reader, and the mark
  // may have moved on another one. Only an item on screen has a badge worth reading.
  const scopes = events.map(({ userId, from, to }) => ({ userId, from, to }))
  if (feedReads || !isOnScreen(itemId, scopes)) return
  refreshBadge(client, itemId, scopes)
}

/**
 * The item went with its discussion — deleted here, or told by an event: its open feeds close, and the message being
 * written for it goes. A section on screen keeps that text to be copied, and drops it as it leaves (§14).
 */
export function closeFeedsOf(itemId: string): void {
  feeds.get(itemId)?.forEach((feed) => feed.removed())
  forgetHiddenDraft(itemId)
}

/** A batch of realtime events: the discussion's go here, the calendar's to its own refresh. */
export function handleDiscussionEvents(client: ApolloClient, events: readonly RealtimeEvent[]): void {
  const byItem = new Map<string, DiscussionChangedEvent[]>()
  for (const event of events) {
    if (event.type !== "discussion.changed") continue
    byItem.set(event.itemId, [...(byItem.get(event.itemId) ?? []), event])
  }
  if (byItem.size === 0) return
  byItem.forEach((itemEvents, itemId) => forItem(client, itemId, itemEvents))
  refreshAthleteBadges()
}

/**
 * The channel could not replay what it missed: every open feed reads its range again and the
 * athletes' badges are read again. The items' badges come back with the calendar's own reload.
 */
export function reloadDiscussions(): void {
  feeds.forEach((set) => set.forEach((feed) => feed.reload()))
  refreshAthleteBadges()
}

/** Test seam — the registrations outlive a test file otherwise. */
export function resetDiscussionEventsForTests(): void {
  feeds.clear()
  athleteBadges.clear()
  newMessageListeners.clear()
}
